// Patient web and the workforce dashboard share an origin. Keep legacy storage
// with patient web; a dashboard sign-in creates a separate namespace.
export const PORTAL_STORAGE_PREFIX = 'doctor-dashboard:portal:';
export const PORTAL_GENERATION_KEY = 'doctor_dashboard_portal_generation';
const GENERATION_STORAGE_PREFIX = PORTAL_STORAGE_PREFIX + 'sessions:';

// Coordination only: no credentials or PHI. A persistent marker avoids a shared
// localStorage pointer that an old logout could delete after a fresh handoff.
export function readPortalGeneration(): string | null {
  const values = document.cookie.split(';').map(part => part.trim())
    .filter(part => part.startsWith(PORTAL_GENERATION_KEY + '='));
  if (!values.length) return null;
  if (values.length !== 1) return 'replacing:invalid';
  try {
    const value = decodeURIComponent(values[0].slice(PORTAL_GENERATION_KEY.length + 1));
    return /^(?:active|replacing):[a-zA-Z0-9-]+$/.test(value) ? value : 'replacing:invalid';
  } catch { return 'replacing:invalid'; }
}

export function writePortalGeneration(value: string) {
  document.cookie = `${PORTAL_GENERATION_KEY}=${encodeURIComponent(value)}; Path=/; Max-Age=31536000; SameSite=Strict${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  if (readPortalGeneration() !== value) throw new Error('Browser session coordination requires cookies');
}

export function portalGenerationPrefix(generation: string | null): string {
  if (generation?.startsWith('active:')) return GENERATION_STORAGE_PREFIX + generation.slice(7) + ':';
  return generation ? GENERATION_STORAGE_PREFIX + 'blocked:' + generation + ':' : PORTAL_STORAGE_PREFIX;
}

export function getPortalStoragePrefix(): string { return portalGenerationPrefix(documentGeneration); }
let portalDocument = false;
let closed = false;
let documentGeneration: string | null = null;
let recoveryDocument = false;
export function checkPortalStorage() { return currentPortalGeneration(); }

// Clean only proven portal generations under the same lock as handoff. Cookie
// loss or a failed preparation must not strand prior account PHI indefinitely.
export async function cleanupAbandonedPortalGenerations(): Promise<void> {
  if (!navigator.locks) return;
  await navigator.locks.request(PORTAL_STORAGE_PREFIX + 'handoff', { mode: 'exclusive' }, () => {
    const current = readPortalGeneration();
    const active = current?.startsWith('active:') ? portalGenerationPrefix(current) : null;
    for (const raw of [window.localStorage, window.sessionStorage]) {
      for (const key of Array.from({ length: raw.length }, (_, index) => raw.key(index))) {
        if (key?.startsWith(GENERATION_STORAGE_PREFIX) && (!active || !key.startsWith(active))) raw.removeItem(key);
      }
    }
  });
}

export function isPatientPath(path: string): boolean {
  // A dated/anchored root is explicit patient intent. Bare '/' still uses
  // account discovery, including the workforce router's relative home path.
  return /^\/[?#]/.test(path) || /^\/(?:dashboard\/)?p(?:\/|[?#]|$)/.test(path);
}

export function isPatientSurface(path: string, search = '', legacySignIn = false): boolean {
  if (isPatientPath(path)) return true;
  const params = new URLSearchParams(search);
  if (/\/(?:dashboard\/)?sign-in$/.test(path) && params.has('portal_notice')) return false;
  const next = params.get('next');
  return /\/(?:dashboard\/)?sign-in$/.test(path) &&
    (next !== null ? isPatientPath(next) : legacySignIn);
}

export function configureAppStorage(path = window.location.pathname, search = window.location.search) {
  // An ambiguous old credential is patient-only. Default sign-in can still
  // restore it; entering a workforce route always starts a portal document.
  const legacySignIn = ['authToken', 'refreshToken', 'auth_token', 'refresh_token']
    .some(key => window.localStorage.getItem(key) || window.sessionStorage.getItem(key));
  const rootPatientSurface = !/^\/dashboard(?:\/|$)/.test(path);
  portalDocument = !(rootPatientSurface || isPatientSurface(path, search, legacySignIn));
  closed = false;
  documentGeneration = readPortalGeneration();
  recoveryDocument = portalDocument && documentGeneration?.startsWith('replacing:') === true &&
    /\/(?:dashboard\/)?sign-in$/.test(path) && new URLSearchParams(search).get('portal_notice') === 'signed_out';
  if (portalDocument) void cleanupAbandonedPortalGenerations().catch(() => undefined);
}

export function isPortalDocument() { return portalDocument; }
export function permitFreshPortalRecovery(generation: string | null) {
  return recoveryDocument && generation === documentGeneration && readPortalGeneration() === generation;
}

function currentPortalGeneration(): boolean {
  if (closed) return false;
  if (!portalDocument) return true;
  const current = readPortalGeneration();
  if (current === documentGeneration && !current?.startsWith('replacing:')) {
    return true;
  }
  if (current !== documentGeneration) recoveryDocument = false;
  // A different document owns the replacement namespace. Close this tab only:
  // never erase or revoke the replacement session, even before storage events.
  closed = true;
  for (const key of Array.from({ length: window.sessionStorage.length }, (_, i) => window.sessionStorage.key(i))) {
    const prefix = getPortalStoragePrefix();
    if (key?.startsWith(prefix) && (prefix !== PORTAL_STORAGE_PREFIX || !key.startsWith(GENERATION_STORAGE_PREFIX))) window.sessionStorage.removeItem(key);
  }
  window.dispatchEvent(new Event('portal:ended'));
  // Preparation never exposes a half-written bundle or loops reloads. Wait
  // for the synchronous handoff to commit; a failed cutover returns to login.
  const target = window.location.href;
  const started = Date.now();
  const reset = () => {
    if (readPortalGeneration()?.startsWith('replacing:') && Date.now() - started < 5000) {
      setTimeout(reset, 50); return;
    }
    const preparing = readPortalGeneration()?.startsWith('replacing:');
    const currentUrl = new URL(window.location.href);
    if (preparing && /\/(?:dashboard\/)?sign-in$/.test(currentUrl.pathname) && currentUrl.searchParams.get('portal_notice') === 'signed_out') return;
    window.location.replace(preparing ? '/dashboard/sign-in?portal_notice=signed_out' : target);
  };
  reset();
  return false;
}

export function createScopedStorage(storage: Storage, prefix: string): Storage {
  const owned = (key: string) => key.startsWith(prefix) &&
    (prefix !== PORTAL_STORAGE_PREFIX || !key.startsWith(GENERATION_STORAGE_PREFIX));
  const guarded = portalDocument && prefix === getPortalStoragePrefix();
  const usable = () => guarded ? currentPortalGeneration() : !closed;
  const keys = () => !usable() ? [] : Array.from({ length: storage.length }, (_, index) => storage.key(index))
    .filter((key): key is string => key !== null && owned(key));
  const cleanupAfterRace = () => {
    if (!guarded || currentPortalGeneration()) return;
    // Physical ownership stays fixed even if replacement occurs between the
    // check and mutation. Only the old generation can be removed here.
    for (const key of Array.from({ length: storage.length }, (_, index) => storage.key(index))) {
      if (key !== null && owned(key)) storage.removeItem(key);
    }
  };
  return {
    get length() { return keys().length; },
    key(index) { return keys()[index]?.slice(prefix.length) ?? null; },
    getItem(key) {
      if (!usable() || !owned(prefix + key)) return null;
      const value = storage.getItem(prefix + key);
      return usable() ? value : null;
    },
    setItem(key, value) {
      if (!usable() || !owned(prefix + key)) return;
      storage.setItem(prefix + key, String(value));
      cleanupAfterRace();
    },
    removeItem(key) {
      if (!usable() || !owned(prefix + key)) return;
      storage.removeItem(prefix + key);
      cleanupAfterRace();
    },
    clear() { if (!usable()) return; keys().forEach(key => storage.removeItem(key)); cleanupAfterRace(); },
  };
}

export function getAppLocalStorage(): Storage {
  return portalDocument ? createScopedStorage(window.localStorage, getPortalStoragePrefix()) : window.localStorage;
}

export function getAppSessionStorage(): Storage {
  return portalDocument ? createScopedStorage(window.sessionStorage, getPortalStoragePrefix()) : window.sessionStorage;
}

export function appStorageEventKey(key: string | null): string | null {
  if (!portalDocument) return key?.startsWith(PORTAL_STORAGE_PREFIX) ? null : key;
  const prefix = getPortalStoragePrefix();
  return key?.startsWith(prefix) && (prefix !== PORTAL_STORAGE_PREFIX || !key.startsWith(GENERATION_STORAGE_PREFIX)) ? key.slice(prefix.length) : null;
}

export function closePortalStorage() {
  recoveryDocument = false;
  if (!portalDocument) return;
  getAppLocalStorage().clear();
  getAppSessionStorage().clear();
  closed = true;
  void cleanupAbandonedPortalGenerations().catch(() => undefined);
}

// Shared build chunks can evaluate storage consumers before main's body.
// Select the namespace at module evaluation as well as at the entry boundary.
if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') configureAppStorage();

if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
  const channel = new BroadcastChannel(PORTAL_STORAGE_PREFIX + 'generation');
  channel.addEventListener('message', () => { if (portalDocument) currentPortalGeneration(); });
}
